import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Form,
  Select,
  Button,
  Spin,
  message,
  Typography,
  Switch,
  TimePicker,
} from "antd";
import { FrownOutlined, SaveOutlined } from "@ant-design/icons";
import {
  get,
  put,
  post,
  del,
} from "../../../Api/http/httpClient";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import PhysicalPointModal, { PhysicalPoint } from "../../../components/modals/PhysicalPointModal";
import "./Schedule.css";

dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);

const { Text, Title } = Typography;

// Mapeo de días de inglés a español
const dayTranslations: { [key: string]: string } = {
  monday: "Lunes",
  tuesday: "Martes",
  wednesday: "Miércoles",
  thursday: "Jueves",
  friday: "Viernes",
  saturday: "Sábado",
  sunday: "Domingo",
};

// Opciones de días para el formulario
const daysOptions = Object.keys(dayTranslations).map((key) => ({
  value: key,
  label: dayTranslations[key],
}));

const Schedule: React.FC<{ user_data: any }> = ({ user_data }) => {
  const [loading, setLoading] = useState(true);
  // actionLoading se usará para mostrar la animación en peticiones (update, delete, create)
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(false);
  const [form] = Form.useForm();
  const [originalValues, setOriginalValues] = useState<any>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [physicalPoints, setPhysicalPoints] = useState<PhysicalPoint[]>([]);

  // Estados para el modal de puntos físicos
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPoint, setEditingPoint] = useState<PhysicalPoint | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const screens = useBreakpoint();

  // Función para convertir las horas del backend al formato interno
  const convertTimeFromBackend = (timeString: string) => {
    return dayjs(timeString, "hh:mm A");
  };

  /**
   * Formatea un arreglo de horarios a formato "HH:MM AM/PM - HH:MM AM/PM"
   */
  const formatSchedule = (schedule: string[] | undefined): string => {
    if (!schedule || schedule.length !== 2) {
      return "Horario no disponible";
    }
    const [openTime, closeTime] = schedule;
    const formattedOpenTime = dayjs(openTime, "HH:mm").format("hh:mm A");
    const formattedCloseTime = dayjs(closeTime, "HH:mm").format("hh:mm A");
    return `${formattedOpenTime} - ${formattedCloseTime}`;
  };

  /**
   * Carga la información inicial del establecimiento:
   *  - Si es e-commerce, obtiene los puntos físicos de la API.
   *  - Si no es e-commerce, obtiene los horarios de "restaurant-onboarding".
   */
  const fetchEstablishmentData = async () => {
    setLoading(true);
    setError(false);
    try {
      if (user_data["custom:establishment"] === "e-commerce") {
        const response = await get(
          `physical-points?establishmentID=${user_data["custom:establishment_id"]}&name_establishment=${user_data.family_name}`
        );
        if (response.answer) {
          setPhysicalPoints(response.items || []);
        } else {
          setError(true);
          message.error("No se encontraron puntos físicos para este establecimiento.");
        }
      } else {
        const response = await get(
          `restaurant-onboarding?establishmentID=${user_data["custom:establishment_id"]}&name_establishment=${user_data.family_name}`
        );
        if (response.answer) {
          const fetchedData = response.item;
          const initialValues = {
            openTime: fetchedData.openTime
              ? convertTimeFromBackend(fetchedData.openTime)
              : null,
            closeTime: fetchedData.closeTime
              ? convertTimeFromBackend(fetchedData.closeTime)
              : null,
            specialHours: fetchedData.specialHours,
            specialOpenTime: fetchedData.specialOpenTime
              ? convertTimeFromBackend(fetchedData.specialOpenTime)
              : null,
            specialCloseTime: fetchedData.specialCloseTime
              ? convertTimeFromBackend(fetchedData.specialCloseTime)
              : null,
            openDays: fetchedData.openDays || [],
          };
          form.setFieldsValue(initialValues);
          setOriginalValues(initialValues);
        } else {
          setError(true);
          message.error("No se encontraron datos del establecimiento.");
        }
      }
    } catch (error) {
      setError(true);
      message.error("Error al obtener los datos del establecimiento.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEstablishmentData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onValuesChange = (changedValues: any, allValues: any) => {
    const isDifferent = Object.keys(originalValues).some((key) => {
      if (
        (key === "openTime" ||
          key === "closeTime" ||
          key === "specialOpenTime" ||
          key === "specialCloseTime") &&
        originalValues[key] &&
        allValues[key]
      ) {
        return !originalValues[key].isSame(allValues[key], "minute");
      }
      return originalValues[key] !== allValues[key];
    });
    setHasChanges(isDifferent);
  };

  const formatTimeForBackend = (time: any) => {
    return time ? dayjs(time).format("hh:mm A") : null;
  };

  /**
   * Maneja el envío de los horarios del establecimiento (no e-commerce).
   * Envía la data actualizada a la API "restaurant-onboarding".
   */
  const onFinish = async (values: any) => {
    const changes = Object.keys(values).reduce((acc: any, key) => {
      if (
        key === "openTime" ||
        key === "closeTime" ||
        key === "specialOpenTime" ||
        key === "specialCloseTime"
      ) {
        const originalTime = originalValues[key];
        const newTime = values[key];
        if (
          (originalTime && newTime && !originalTime.isSame(newTime, "minute")) ||
          (originalTime && !newTime) ||
          (!originalValues[key] && newTime)
        ) {
          acc[key] = formatTimeForBackend(newTime);
        }
      } else {
        if (values[key] !== originalValues[key]) {
          acc[key] = values[key];
        }
      }
      return acc;
    }, {});
    if (Object.keys(changes).length === 0) {
      message.info("No se han realizado cambios.");
      return;
    }
    try {
      setActionLoading(true);
      await put("restaurant-onboarding", {
        establishmentID: user_data["custom:establishment_id"],
        name_establishment: user_data.family_name,
        ...changes,
      });
      message.success("Horarios actualizados correctamente.");
      fetchEstablishmentData();
      setHasChanges(false);
    } catch (error) {
      message.error("Error al actualizar los horarios del establecimiento.");
    } finally {
      setActionLoading(false);
    }
  };

  // ---------------- MANEJO DE PUNTOS FÍSICOS (E-COMMERCE) ----------------

  const handleAddPhysicalPoint = () => {
    setEditingPoint(null);
    setEditingIndex(null);
    setModalVisible(true);
  };

  const handleEditPhysicalPoint = (index: number) => {
    const point = physicalPoints[index];
    setEditingPoint(point);
    setEditingIndex(index);
    setModalVisible(true);
  };

  const handleDeletePhysicalPoint = async (index: number) => {
    try {
      setActionLoading(true);
      const pointToDelete = physicalPoints[index];
      const response = await del("physical-points", {
        establishmentID: user_data["custom:establishment_id"],
        name_establishment: user_data.family_name,
        address_to_delete: pointToDelete.address,
        phone_to_delete: pointToDelete.phone,
      });
      if (response.answer) {
        message.success("Punto físico eliminado correctamente.");
        const updatedPoints = physicalPoints.filter((_, i) => i !== index);
        setPhysicalPoints(updatedPoints);
      } else {
        message.error("No se pudo eliminar el punto físico.");
      }
    } catch (error) {
      message.error("Error al eliminar el punto físico.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setEditingPoint(null);
    setEditingIndex(null);
  };

  const handleModalSubmit = async (values: PhysicalPoint) => {
    try {
      setActionLoading(true);
      if (editingIndex !== null) {
        try {
          const oldAddress = physicalPoints[editingIndex].address;
          const oldPhone = physicalPoints[editingIndex].phone;
          const response = await put("physical-points", {
            establishmentID: user_data["custom:establishment_id"],
            name_establishment: user_data.family_name,
            old_address: oldAddress,
            old_phone: oldPhone,
            new_address: values.address,
            new_phone: values.phone,
            new_schedule: values.schedule,
            new_days: values.days,
          });
          if (response.answer) {
            const updatedPoints = [...physicalPoints];
            updatedPoints[editingIndex] = values;
            setPhysicalPoints(updatedPoints);
            message.success("Punto físico actualizado correctamente.");
          } else {
            message.error("Error al actualizar el punto físico.");
            return;
          }
        } catch (error) {
          message.error("Error de comunicación al actualizar el punto físico.");
          return;
        }
      } else {
        try {
          const response = await post("physical-points", {
            establishmentID: user_data["custom:establishment_id"],
            name_establishment: user_data.family_name,
            address: values.address,
            phone: values.phone,
            schedule: values.schedule,
            days: values.days,
          });
          if (response.answer) {
            setPhysicalPoints([...physicalPoints, values]);
            message.success("Punto físico agregado correctamente.");
          } else if (response.statusCode === 409) {
            message.warning("La dirección ya existe en este establecimiento.");
          } else {
            message.error("Error al crear el punto físico.");
            return;
          }
        } catch (error) {
          message.error("Error de comunicación al crear el punto físico.");
          return;
        }
      }
    } finally {
      setActionLoading(false);
      setModalVisible(false);
      setEditingPoint(null);
      setEditingIndex(null);
    }
  };

  const specialHoursValue = Form.useWatch("specialHours", form);

  return (
    <Row justify="center" style={{ marginTop: "20px" }}>
      <Col span={screens.xs ? 24 : 22}>
        <Card
          title={
            user_data["custom:establishment"] === "e-commerce"
              ? "Puntos Físicos y Horarios"
              : "Horarios del Establecimiento"
          }
          extra={
            user_data["custom:establishment"] === "e-commerce" && (
              <Button type="primary" onClick={handleAddPhysicalPoint}>
                Agregar Punto Físico
              </Button>
            )
          }
          bordered={false}
          style={{
            borderRadius: "10px",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
            padding: screens.xs ? "10px" : "10px",
          }}
        >
          {loading ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "300px",
              }}
            >
              <Spin size="large" />
              <Text style={{ marginTop: "20px" }}>
                Cargando datos del establecimiento...
              </Text>
            </div>
          ) : user_data["custom:establishment"] !== "e-commerce" ? (
            <Spin spinning={actionLoading}>
              <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                onValuesChange={onValuesChange}
              >
                <Row gutter={24}>
                  <Col span={screens.md ? 12 : 24}>
                    <Form.Item
                      label="Hora de Apertura"
                      name="openTime"
                      rules={[
                        {
                          required: true,
                          message: "Por favor seleccione la hora de apertura",
                        },
                      ]}
                    >
                      <TimePicker
                        use12Hours
                        format="hh:mm A"
                        size="large"
                        style={{ borderRadius: "8px", width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={screens.md ? 12 : 24}>
                    <Form.Item
                      label="Hora de Cierre"
                      name="closeTime"
                      rules={[
                        {
                          required: true,
                          message: "Por favor seleccione la hora de cierre",
                        },
                      ]}
                    >
                      <TimePicker
                        use12Hours
                        format="hh:mm A"
                        size="large"
                        style={{ borderRadius: "8px", width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={24}>
                  <Col span={24}>
                    <Form.Item
                      label="Días de Apertura"
                      name="openDays"
                      rules={[
                        {
                          required: true,
                          message: "Por favor seleccione los días de apertura",
                        },
                      ]}
                    >
                      <Select
                        mode="multiple"
                        size="large"
                        placeholder="Seleccione los días de apertura"
                        style={{ borderRadius: "8px" }}
                      >
                        {daysOptions.map((day) => (
                          <Select.Option key={day.value} value={day.value}>
                            {day.label}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={24}>
                  <Col span={24}>
                    <Form.Item
                      label="Horarios Especiales"
                      name="specialHours"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                </Row>

                {specialHoursValue && (
                  <Row gutter={24}>
                    <Col span={screens.md ? 12 : 24}>
                      <Form.Item
                        label="Hora Especial de Apertura"
                        name="specialOpenTime"
                        rules={[
                          {
                            required: true,
                            message:
                              "Por favor seleccione la hora especial de apertura",
                          },
                        ]}
                      >
                        <TimePicker
                          use12Hours
                          format="hh:mm A"
                          size="large"
                          style={{ borderRadius: "8px", width: "100%" }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={screens.md ? 12 : 24}>
                      <Form.Item
                        label="Hora Especial de Cierre"
                        name="specialCloseTime"
                        rules={[
                          {
                            required: true,
                            message:
                              "Por favor seleccione la hora especial de cierre",
                          },
                        ]}
                      >
                        <TimePicker
                          use12Hours
                          format="hh:mm A"
                          size="large"
                          style={{ borderRadius: "8px", width: "100%" }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                )}

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    loading={actionLoading}
                    disabled={!hasChanges}
                    icon={<SaveOutlined />}
                    block={screens.xs}
                  >
                    Guardar Cambios
                  </Button>
                </Form.Item>
              </Form>
            </Spin>
          ) : (
            // Para establecimientos e-commerce, se muestra la lista sin spinner global.
            <div>
              <Title level={4}>Puntos Físicos</Title>
              {physicalPoints.length > 0 ? (
                physicalPoints.map((point, index) => (
                  <Card
                    key={index}
                    type="inner"
                    title={`Punto Físico ${index + 1}`}
                    extra={
                      <>
                        <Button
                          type="link"
                          onClick={() => handleEditPhysicalPoint(index)}
                        >
                          Editar
                        </Button>
                        <Button
                          type="link"
                          danger
                          onClick={() => handleDeletePhysicalPoint(index)}
                        >
                          Eliminar
                        </Button>
                      </>
                    }
                    style={{ marginBottom: "16px" }}
                  >
                    <p>
                      <strong>Dirección:</strong> {point.address}
                    </p>
                    <p>
                      <strong>Horario:</strong>{" "}
                      {formatSchedule(point.schedule)}
                    </p>
                    <p>
                      <strong>Teléfono:</strong> {point.phone}
                    </p>
                    <p>
                      <strong>Días:</strong>{" "}
                      {point.days
                        .map((day: string) => dayTranslations[day])
                        .join(", ")}
                    </p>
                  </Card>
                ))
              ) : (
                <Text>No hay puntos físicos registrados.</Text>
              )}
            </div>
          )}
        </Card>
      </Col>

      {/* Modal para crear/editar puntos físicos en e-commerce */}
      {user_data["custom:establishment"] === "e-commerce" && (
        <PhysicalPointModal
          visible={modalVisible}
          onCancel={handleModalCancel}
          onSubmit={handleModalSubmit}
          initialValues={editingPoint || undefined}
          loading={actionLoading}
          is_schedule={true}
        />
      )}
    </Row>
  );
};

export default Schedule;